import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Navigation from '@/components/marketing/Navigation';
import Footer from '@/components/marketing/Footer';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    phone: '',
    preferred_date: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/api/walkthrough-booking`, formData);
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    }
    setIsSubmitting(false);
  };

  const whatToExpect = [
    '30-minute video call',
    'Live walkthrough of Client 360',
    'Discussion of your current setup',
    'Q&A on permissioning, links, and workflow',
    'Pilot scoping if there\'s a fit'
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#FAF7F2]">
        <SEO page="contact" />
        <Navigation />
        <div className="pt-32 pb-20 px-6 lg:px-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 bg-[#A86A3A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#A86A3A]" />
            </div>
            <h1 className="hero-medium text-[#1A1411] mb-4">Walkthrough request received</h1>
            <p className="text-[#6B5B52] mb-8">
              We&apos;ll confirm your preferred time within 1 business day. Looking forward to speaking with you.
            </p>
            <Link to="/" className="btn-secondary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="contact-page">
      <SEO page="contact" />
      <Navigation />
      
      <div className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-[#5B3627] hover:text-[#3d2419] mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h1 className="hero-medium text-[#1A1411] mb-4">Book a walkthrough</h1>
              <p className="text-[#6B5B52] mb-8">
                See how QIndex One can work for your team in a 30-minute call.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="organisation" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Organisation *
                  </label>
                  <input
                    type="text"
                    id="organisation"
                    name="organisation"
                    required
                    value={formData.organisation}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="contact-org-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="preferred_date" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Preferred date/time (optional)
                  </label>
                  <input
                    type="text"
                    id="preferred_date"
                    name="preferred_date"
                    placeholder="e.g., Next Tuesday afternoon"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="contact-date-input"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Message (optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full justify-center"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Request walkthrough'
                  )}
                </button>

                <p className="text-xs text-[#9B8B7E] text-center">
                  We&apos;ll confirm your walkthrough time within 1 business day.
                </p>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:pt-16">
              <div className="bg-[#EFE6DC] p-8">
                <h3 className="heading-3 text-[#1A1411] mb-6">What to expect</h3>
                <ul className="space-y-4">
                  {whatToExpect.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#A86A3A] mt-0.5 flex-shrink-0" />
                      <span className="text-[#6B5B52] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 p-8 border border-[#D8CBBE]">
                <h3 className="heading-3 text-[#1A1411] mb-4">Contact directly</h3>
                <p className="text-[#6B5B52] text-sm mb-4">
                  Prefer to reach out directly?
                </p>
                <a 
                  href="mailto:contact@qindexone.com" 
                  className="text-[#5B3627] hover:text-[#3d2419] font-medium"
                >
                  contact@qindexone.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
